import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { translations } from "../i18n/translations";
import api from "../lib/api";

const AppContext = createContext(null);

const LANG_KEY = "svrm_lang";
const CART_KEY = "svrm_cart";
const TOKEN_KEY = "svrm_token";

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || "en");
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CART_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [admin, setAdmin] = useState(null);
  const [bootChecked, setBootChecked] = useState(false);

  useEffect(() => {
    localStorage.setItem(LANG_KEY, lang);
  }, [lang]);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setBootChecked(true);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setAdmin(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setAdmin(null);
      })
      .finally(() => setBootChecked(true));
  }, []);

  const t = translations[lang];
  const toggleLang = () => setLang((l) => (l === "en" ? "te" : "en"));

  const cartKeyOf = (item) => `${item.product_id}_${item.weight_kg}`;

  const addToCart = useCallback((item) => {
    setCart((prev) => {
      const key = cartKeyOf(item);
      const existing = prev.find((p) => cartKeyOf(p) === key);
      if (existing) {
        return prev.map((p) =>
          cartKeyOf(p) === key ? { ...p, quantity: p.quantity + item.quantity } : p
        );
      }
      return [...prev, item];
    });
  }, []);

  const updateQty = useCallback((key, qty) => {
    setCart((prev) =>
      prev
        .map((p) => (cartKeyOf(p) === key ? { ...p, quantity: Math.max(1, qty) } : p))
        .filter((p) => p.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((key) => {
    setCart((prev) => prev.filter((p) => cartKeyOf(p) !== key));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartTotal = cart.reduce(
    (sum, it) => sum + it.weight_kg * it.price_per_kg * it.quantity,
    0
  );
  const cartCount = cart.reduce((sum, it) => sum + it.quantity, 0);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setAdmin(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setAdmin(null);
  }, []);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      toggleLang,
      t,
      cart,
      addToCart,
      updateQty,
      removeItem,
      clearCart,
      cartTotal,
      cartCount,
      cartKeyOf,
      admin,
      login,
      logout,
      bootChecked,
    }),
    [lang, t, cart, addToCart, updateQty, removeItem, clearCart, cartTotal, cartCount, admin, login, logout, bootChecked]
  );

  return (
    <AppContext.Provider value={value}>{children}</AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
